import { LayoutEditorShell } from '../../../lib/form-layout/LayoutEditorShell';
import {
  WELL_FORM_FIELDS,
  WELL_FORM_FIELD_NAMES,
  WELL_DEFAULT_LAYOUT,
  type WellFormContext,
} from '../../../lib/form-layout/well-fields';
import { useAdminActiveWellLayout, useUpdateWellFormLayout } from '../../../queries/well-form-layout';
import { useLandLookups } from '../../../queries/land-lookups';
import type { WellInputSchema } from '../../../lib/schemas';

/**
 * Thin wrapper around LayoutEditorShell that wires the well-form's
 * registry / queries / preview context. Mechanical clone of
 * ContractLandFormLayoutEditor.tsx / LeaseFormLayoutEditor.tsx.
 *
 * The well registry's lookup-backed selects (well_status, well_type,
 * unit_type, fed_rights, flag_for_issues) read their options off
 * `context.lookups` — same pattern as the entry editor threading
 * `docTypes`/`prospects` through `previewContext`. The query hook is called
 * here, once, and never inside the renderer's per-field loop (which would
 * break the Rules of Hooks).
 */
export function WellFormLayoutEditor() {
  const { data: lookups } = useLandLookups();

  const previewDefaults: WellInputSchema = {
    property_number: '',
    property_name: null,
    api: null,
    alt_prop_number: null,
    prospect: null,
    field: null,
    rsrv_cat: null,
    abstract: null,
    survey: null,
    roll_up: false,
    reviewer_user_id: null,

    well_status: null,
    well_type: null,
    unit_type: null,
    fed_rights: null,
    unit_acreage: null,
    do_number: null,
    owner: null,
    operator: null,

    county: null,
    state: null,
    township: null,
    range: null,
    section: null,
    block: null,

    description: null,
    flag_for_issues: null,
    exception_remark: null,
    title_issues: null,

    oil_bpo_wi: null,
    oil_apo_wi: null,
    oil_bpo_nri: null,
    oil_apo_nri: null,
    oil_bpo_orri: null,
    oil_apo_orri: null,
    oil_bpo_ri: null,
    oil_apo_ri: null,
    gas_bpo_wi: null,
    gas_apo_wi: null,
    gas_bpo_nri: null,
    gas_apo_nri: null,
    gas_bpo_orri: null,
    gas_apo_orri: null,
    gas_bpo_ri: null,
    gas_apo_ri: null,

    minerals: false,
    royalties: false,
    participating: false,
    non_participating: false,
    term: false,
    perpetual: false,
    call_on_prod: false,
    non_consent: false,
    non_consent_penalty: null,
    payout: false,
    payout_amount: null,
    payout_date: null,

    first_prod: null,
    last_prod: null,
    oil_data_as_of: null,
    oil_cum: null,
    oil_avg_bpm_6mo: null,
    oil_avg_bopd_6mo: null,
    gas_data_as_of: null,
    gas_cum: null,
    gas_avg_mcf_6mo: null,
    gas_avg_mcfd_6mo: null,
    num_completions: null,
    total_depth: null,
    prod_zones: null,

    remarks: null,
    reviewer_notes: null,
    change_remarks: null,
  };

  const previewContext: WellFormContext = {
    lookups: lookups ?? {
      lease_type: [],
      lease_status: [],
      lease_ownership: [],
      fed_rights: [],
      contract_type: [],
      flag_for_issues: [],
      well_status: [],
      well_type: [],
      unit_type: [],
    },
    // The layout editor's preview form has no real project/session behind
    // it, so the reviewer Select has no members to offer — canAssignAnyReviewer:
    // true just keeps the (empty) option list unfiltered rather than
    // collapsing to a self-only view that can never match anything here.
    members: [],
    canAssignAnyReviewer: true,
    currentUserId: 'preview',
  };

  return (
    <LayoutEditorShell
      title="Well form layout"
      breadcrumbLabel="Well form layout"
      registry={WELL_FORM_FIELDS}
      allNames={WELL_FORM_FIELD_NAMES}
      defaultLayout={WELL_DEFAULT_LAYOUT}
      useActive={useAdminActiveWellLayout}
      useUpdate={useUpdateWellFormLayout}
      previewDefaults={previewDefaults}
      previewContext={previewContext}
    />
  );
}
